
































































































































































































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import {
  BookingConfirmation,
  BookingRequest,
  Customer,
  Shop,
  ShopAggregations,
  UnconfirmedBookingAction
} from "../../api/types";
import SbModal from "@/components/Modal.vue";
import SbSpinner from "@/components/Spinner.vue";
import EmailDidYouMean from "@/components/EmailDidYouMean.vue";
import {
  EmailSuggestion,
  sanitizeEmailInput,
  suggestEmailCorrection
} from "@/utils/emailSuggestion";
import {
  cancelUnconfirmedBooking,
  confirmUnconfirmedBooking,
  createBookingCustomer,
  getBookingsAvailability
} from "@/api/bookings";
import {
  bookingRequiresPayment,
  completePaidBookingCheckout
} from "@/utils/checkout";
import { formatDateReadable, formatTimeReadable } from "@/utils/time";

type FlowStep = "details" | "summary";
type CreateState = "idle" | "creating" | "ready" | "error";

@Component({
  components: {
    SbModal,
    SbSpinner,
    EmailDidYouMean
  }
})
export default class ShopMenuConfirmModal extends Vue {
  @Prop({ default: false }) private open!: boolean;
  @Prop() private bookingRequest!: BookingRequest;
  @Prop() private shopAggregations!: ShopAggregations;
  @Prop() private showAddressField!: boolean;
  @Prop({ default: null }) private shop!: Shop | null;

  private step: FlowStep = "details";
  private customer: Customer = {
    name: "",
    emailAddress: "",
    phoneNumber: "",
    shortAddress: ""
  };
  private createState: CreateState = "idle";
  private createError = "";
  private confirmation: BookingConfirmation | null = null;
  private resolvedResourceId: string | null = null;
  private isFinalizing = false;
  private isCancelling = false;
  private createPromise: Promise<void> | null = null;
  private lastCreated: BookingConfirmation | null = null;
  private draftEmail = "";
  private dismissedOriginal: string | null = null;
  private appliedFlash = false;
  private createGen = 0;
  private appliedFlashTimer: number | null = null;
  private isApplyingEmail = false;
  private lastApplyAt = 0;

  get shopName(): string {
    return (this.shop && this.shop.name) || "";
  }

  get serviceLabel(): string {
    const { serviceId, servicePackageId } =
      this.bookingRequest || ({} as BookingRequest);
    const services =
      (this.shopAggregations && this.shopAggregations.services) || {};
    const service = services[serviceId];
    if (!service) return "";
    const pkg = service.packages && service.packages[servicePackageId];
    if (
      pkg &&
      service.packagesDisplayOrder &&
      service.packagesDisplayOrder.length > 1
    ) {
      return `${service.name} — ${pkg.name}`;
    }
    return service.name;
  }

  get resourceLabel(): string {
    const resourceId =
      this.resolvedResourceId ||
      (this.bookingRequest && this.bookingRequest.resourceId);
    if (!resourceId) return "";
    if (resourceId === "Any") return "Any available";
    const resources =
      (this.shopAggregations && this.shopAggregations.resources) || {};
    const resource = resources[resourceId];
    return (resource && resource.name) || resourceId;
  }

  get whenLabel(): string {
    if (!this.bookingRequest) return "";
    const { startDate, startTime, endTime } = this.bookingRequest;
    const day = formatDateReadable(startDate);
    const start = formatTimeReadable(startTime);
    const end = formatTimeReadable(endTime);
    return `${day} · ${start} – ${end}`;
  }

  get priceLabel(): string {
    if (!this.bookingRequest) return "";
    const services =
      (this.shopAggregations && this.shopAggregations.services) || {};
    const service = services[this.bookingRequest.serviceId];
    if (!service) return "";
    const pkg = service.packages[this.bookingRequest.servicePackageId];
    if (!pkg) return "";
    const price = Number(pkg.price) || 0;
    const fee = this.shop
      ? parseFloat(String(this.shop.onlinePaymentFee || 0)) || 0
      : 0;
    const total = price + fee;
    return `€${total.toFixed(2)}`;
  }

  get requiresPayment(): boolean {
    if (this.confirmation) {
      return bookingRequiresPayment(this.confirmation);
    }
    // Heuristic until create returns — fee shops almost always pay online
    const fee = this.shop
      ? parseFloat(String(this.shop.onlinePaymentFee || 0)) || 0
      : 0;
    return fee > 0;
  }

  /** Soft line under the card while reserve runs (hidden once they press Pay) */
  get showSoftHoldHint(): boolean {
    return (
      this.step === "summary" &&
      this.createState === "creating" &&
      !this.isFinalizing
    );
  }

  get detailsSuggestion(): EmailSuggestion | null {
    return suggestEmailCorrection(this.draftEmail);
  }

  get summarySuggestion(): EmailSuggestion | null {
    return suggestEmailCorrection(this.customer.emailAddress, {
      committed: true
    });
  }

  get showInlineHint(): boolean {
    return !this.isDismissed(this.detailsSuggestion);
  }

  get showSummaryHint(): boolean {
    return !this.appliedFlash && !this.isDismissed(this.summarySuggestion);
  }

  get canPressPrimary(): boolean {
    if (this.step !== "summary") return false;
    if (this.isFinalizing || this.isCancelling) return false;
    // After a failed hold they must use Try again first
    if (this.createState === "error") return false;
    return true;
  }

  get primaryBusyLabel(): string {
    if (!this.isFinalizing) return "";
    if (this.createState === "creating" || this.createState === "idle") {
      return "Reserving your appointment…";
    }
    if (this.requiresPayment) {
      return "Taking you to secure payment…";
    }
    return "Confirming your appointment…";
  }

  get primaryButtonLabel(): string {
    if (this.isFinalizing && this.primaryBusyLabel) {
      return this.primaryBusyLabel;
    }
    if (this.requiresPayment) {
      return "Pay now";
    }
    return "Confirm booking";
  }

  @Watch("open")
  onOpenChange(isOpen: boolean) {
    if (isOpen) {
      this.resetFlow();
    }
  }

  beforeDestroy() {
    this.clearAppliedFlashTimer();
  }

  private resetFlow() {
    this.step = "details";
    this.createState = "idle";
    this.createError = "";
    this.confirmation = null;
    this.resolvedResourceId = null;
    this.isFinalizing = false;
    this.isCancelling = false;
    this.createPromise = null;
    this.lastCreated = null;
    this.draftEmail = "";
    this.dismissedOriginal = null;
    this.appliedFlash = false;
    this.isApplyingEmail = false;
    this.lastApplyAt = 0;
    this.createGen += 1;
    this.clearAppliedFlashTimer();
    this.customer = {
      name: "",
      emailAddress: "",
      phoneNumber: "",
      shortAddress: ""
    };
  }

  private isDismissed(suggestion: EmailSuggestion | null): boolean {
    if (!suggestion) return true;
    return this.dismissedOriginal === suggestion.original.toLowerCase();
  }

  private clearAppliedFlashTimer() {
    if (this.appliedFlashTimer !== null) {
      window.clearTimeout(this.appliedFlashTimer);
      this.appliedFlashTimer = null;
    }
  }

  dismissSuggestion() {
    const suggestion =
      this.step === "summary" ? this.summarySuggestion : this.detailsSuggestion;
    if (!suggestion) return;
    this.dismissedOriginal = suggestion.original.toLowerCase();
  }

  applySuggestedEmail(suggested: string) {
    if (!suggested || this.isApplyingEmail) return;
    const now = Date.now();
    if (now - this.lastApplyAt < 500) return;
    this.lastApplyAt = now;

    this.draftEmail = suggested;
    this.dismissedOriginal = null;
    this.syncFormulateEmail(suggested);
    if (this.step !== "summary") return;

    const previousEmail = this.customer.emailAddress;
    this.customer = { ...this.customer, emailAddress: suggested };
    this.appliedFlash = true;
    this.clearAppliedFlashTimer();
    this.appliedFlashTimer = window.setTimeout(() => {
      this.appliedFlash = false;
      this.appliedFlashTimer = null;
    }, 2200);

    this.isApplyingEmail = true;
    this.refreshHoldAfterEmailChange(previousEmail).then(
      () => {
        this.isApplyingEmail = false;
      },
      () => {
        this.isApplyingEmail = false;
      }
    );
  }

  private syncFormulateEmail(email: string) {
    try {
      (this as any).$formulate.setValues("customerForm", {
        emailAddress: email
      });
    } catch (e) {
      /* form may not be registered */
    }
  }

  private async refreshHoldAfterEmailChange(previousEmail: string) {
    const existing = this.confirmation || this.lastCreated;
    const pending = this.createPromise;
    this.createGen += 1;
    this.createState = "creating";
    this.createError = "";
    this.confirmation = null;

    const run = async () => {
      if (pending) {
        try {
          await pending;
        } catch (e) {
          /* previous create may have failed */
        }
      }
      const toCancel = this.lastCreated || existing;
      if (toCancel && toCancel.cancelToken && this.bookingRequest) {
        try {
          await cancelUnconfirmedBooking({
            bookingId: toCancel.bookingId,
            cancelToken: toCancel.cancelToken,
            storeId: this.bookingRequest.storeId,
            start: this.bookingRequest.start,
            end: this.bookingRequest.end,
            customerEmail: previousEmail
          });
        } catch (e) {
          /* hold may already be gone */
        }
      }
      await this.startCreate();
    };

    this.createPromise = run();
    await this.createPromise;
  }

  onDetailsSubmit(customer: Customer) {
    const emailAddress =
      sanitizeEmailInput(this.draftEmail || customer.emailAddress || "") ||
      customer.emailAddress;
    this.customer = { ...customer, emailAddress };
    this.draftEmail = emailAddress;
    this.step = "summary";
    this.startCreate();
  }

  private async startCreate() {
    const gen = ++this.createGen;
    this.createState = "creating";
    this.createError = "";
    this.confirmation = null;

    const run = async () => {
      try {
        const booking = await this.buildBookingPayload();
        const confirmation = await createBookingCustomer(booking);
        this.lastCreated = confirmation;
        // Ignore late results if user already left summary / reset
        if (gen !== this.createGen || this.step !== "summary") return;
        this.confirmation = confirmation;
        this.resolvedResourceId = booking.resourceId;
        this.createState = "ready";
      } catch (e) {
        console.error(e);
        if (gen !== this.createGen || this.step !== "summary") return;
        this.createState = "error";
        this.createError =
          (e && (e.message || e.details)) ||
          "We couldn’t reserve this appointment. Please try another time.";
      }
    };

    this.createPromise = run();
    await this.createPromise;
  }

  private async buildBookingPayload(): Promise<BookingRequest> {
    const bookingRequest = {
      ...this.bookingRequest,
      customer: this.customer
    };

    if (bookingRequest.resourceId === "Any") {
      bookingRequest.resourceId = await this.allocateResource(
        this.shopAggregations,
        bookingRequest
      );
    }

    return bookingRequest;
  }

  retryCreate() {
    if (this.createState === "creating" || this.isFinalizing) return;
    this.startCreate();
  }

  /**
   * Intent-first: user can press Pay immediately.
   * If the hold is still in flight, wait on the button, then pay/confirm.
   */
  async onFinalize() {
    if (!this.canPressPrimary) return;
    this.isFinalizing = true;

    try {
      // Wait for background reserve if it hasn't finished yet
      if (this.createState === "creating" || this.createState === "idle") {
        if (this.createPromise) {
          await this.createPromise;
        } else {
          await this.startCreate();
        }
      }

      if (this.createState === "error" || !this.confirmation) {
        // Failure already shown under the card
        this.isFinalizing = false;
        return;
      }

      if (bookingRequiresPayment(this.confirmation)) {
        await completePaidBookingCheckout(
          this.confirmation,
          this.bookingRequest.storeId
        );
        // Stripe redirect navigates away
        return;
      }

      const action = this.buildActionClaim();
      if (!action) {
        throw new Error("Missing booking confirmation details");
      }
      const { bookingId } = await confirmUnconfirmedBooking(action);
      this.$router.push(`/booking-confirmation/${bookingId}`);
    } catch (e) {
      console.error(e);
      this.$toast.error((e && e.message) || "Something went wrong");
      this.isFinalizing = false;
    }
  }

  async onEditBooking() {
    if (this.isFinalizing || this.isCancelling) return;
    this.isCancelling = true;

    try {
      if (this.createPromise) {
        await this.createPromise;
      }
      await this.cancelHeldBooking();
      this.step = "details";
      this.createState = "idle";
      this.confirmation = null;
      this.resolvedResourceId = null;
      this.createError = "";
      this.createPromise = null;
      this.lastCreated = null;
      this.appliedFlash = false;
      this.isFinalizing = false;
    } catch (e) {
      console.error(e);
      this.$toast.error(
        (e && e.message) || "Could not release the booking. Please try again."
      );
    } finally {
      this.isCancelling = false;
    }
  }

  goBackFromDetails() {
    this.$emit("cancel");
    this.close();
  }

  private buildActionClaim(): UnconfirmedBookingAction | null {
    if (!this.confirmation || !this.confirmation.cancelToken) return null;
    return {
      bookingId: this.confirmation.bookingId,
      cancelToken: this.confirmation.cancelToken,
      storeId: this.bookingRequest.storeId,
      start: this.bookingRequest.start,
      end: this.bookingRequest.end,
      customerEmail: this.customer.emailAddress
    };
  }

  private async cancelHeldBooking() {
    const action = this.buildActionClaim();
    if (!action) return;
    try {
      await cancelUnconfirmedBooking(action);
    } catch (e) {
      if (this.createState === "error") return;
      throw e;
    }
  }

  close() {
    this.$emit("update:open", false);
  }

  private async allocateResource(
    shopAgg: ShopAggregations,
    booking: BookingRequest
  ) {
    const { services } = shopAgg;
    const service = services[booking.serviceId];
    if (!service || !Array.isArray(service.resources)) {
      return "Different";
    }
    const resources = [...service.resources];
    ShopMenuConfirmModal.shuffle(resources);

    const availableBookingsList = await Promise.all(
      resources.map(resource =>
        getBookingsAvailability(resource, booking.serviceId)
      )
    );

    for (let i = 0; i < resources.length; i++) {
      const availableBookings = availableBookingsList[i];
      if (
        ShopMenuConfirmModal.slotsDefined(
          availableBookings,
          booking.startDate
        ) &&
        availableBookings[booking.startDate].includes(booking.startTime)
      ) {
        return resources[i];
      }
    }

    return "Different";
  }

  private static slotsDefined(availableBookings: any, startDate: string) {
    return (
      availableBookings !== null && availableBookings[startDate] !== undefined
    );
  }

  private static rand(n: number) {
    return Math.floor(Math.random() * n);
  }

  private static swap(t: any[], i: number, j: number) {
    const q = t[i];
    t[i] = t[j];
    t[j] = q;
    return t;
  }

  private static shuffle(resources: string[]) {
    {
      let last = resources.length;
      let n;
      while (last > 0) {
        n = ShopMenuConfirmModal.rand(last);
        ShopMenuConfirmModal.swap(resources, n, --last);
      }
    }
  }
}
